import React, { useState } from 'react';
import { Box, Typography, IconButton, Popover, Button } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const fmtDisplay = (val) =>
  val
    ? new Date(val + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short', day: '2-digit', year: 'numeric',
      })
    : '';

// Years shown per page of the year grid when enableYearNav is on.
const YEAR_GRID_SIZE = 12;

const MS_PER_DAY = 86400000;
const pad2 = (n) => String(n).padStart(2, '0');
const dateStrOf = (year, month, day) => `${year}-${pad2(month + 1)}-${pad2(day)}`;

const msOf = (dateStr) => new Date(`${dateStr}T00:00:00Z`).getTime();
const strOfMs = (ms) => {
  const d = new Date(ms);
  return dateStrOf(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};
const addDaysStr = (dateStr, delta) => strOfMs(msOf(dateStr) + delta * MS_PER_DAY);

// Same epoch-aligned Monday-Sunday formula as GameAnnouncementService.ResolveDateRange (C#)
// and AnnouncementFormModal.jsx's own resolvePeriod — day 0 (1900-01-01, UTC) is a Monday,
// so flooring to the nearest multiple of 7 days always lands on a Monday regardless of
// which day of the week `dateStr` falls on. UTC-based to avoid local-timezone/DST drift,
// same reasoning as those two call sites.
const getWeekRange = (dateStr) => {
  const anchor = msOf(dateStr);
  const epoch = Date.UTC(1900, 0, 1);
  const days = Math.floor((anchor - epoch) / MS_PER_DAY);
  const weekStartMs = epoch + Math.floor(days / 7) * 7 * MS_PER_DAY;
  return { startStr: strOfMs(weekStartMs), endStr: strOfMs(weekStartMs + 6 * MS_PER_DAY) };
};

// First-to-last day of the calendar month `dateStr` falls in.
const getMonthRange = (dateStr) => {
  const [y, m] = dateStr.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { startStr: `${dateStr.slice(0, 8)}01`, endStr: dateStrOf(y, m - 1, lastDay) };
};

const inBand = (band, dateStr) => !!band && !!dateStr && dateStr >= band.startStr && dateStr <= band.endStr;

// A day another record holds (disabledRanges) inside the picked week / month / range: dark red among the navy ones, so the
// admin sees exactly which days make the pick overlap.
const OVERLAP_BG = '#b91c1c';

// A band's rounded "pill" corners for one cell, given its day-of-week column (0=Su..6=Sa)
// in this Sunday-first grid. Neighbor-based (not assuming any particular band shape/length,
// unlike a week) so this works for week, month, or an arbitrary start/end range alike: a
// cell rounds left when it's the first column of a grid row OR the day before it isn't in
// the band, and rounds right the same way using the day after.
const bandCellRadius = (band, dateStr, col) => {
  if (!band) return 0;
  const roundLeft = col === 0 || !inBand(band, addDaysStr(dateStr, -1));
  const roundRight = col === 6 || !inBand(band, addDaysStr(dateStr, 1));
  if (roundLeft && roundRight) return '8px';
  if (roundLeft) return '8px 0 0 8px';
  if (roundRight) return '0 8px 8px 0';
  return '0';
};

const CustomDatePicker = ({
  value = '',
  onChange,
  min,
  max,
  placeholder = 'Select date',
  isError = false,
  helperText = '',
  fullWidth = false,
  // Opt-in month/year jump. Default off so every existing consumer renders exactly as
  // before — the header stays plain text and the new code path is unreachable.
  // Needed where the range is unbounded: month chevrons alone are ~80 clicks to 2020.
  enableYearNav = false,
  // Shows a value the admin can't edit (e.g. a server-computed date) as the same real
  // field shape as an editable one, instead of a plain text preview — the popover just
  // never opens.
  disabled = false,
  // Opt-in: highlights the whole Monday-Sunday week a day belongs to, on hover and for
  // the selected value — for a "which week?" picker (e.g. the Game Announcement Weekly
  // period field) where a single day's own highlight doesn't make clear that picking any
  // day in a week selects that entire week. Off by default so every other consumer of
  // this shared component renders exactly as before.
  highlightWeek = false,
  // Same idea as highlightWeek, but highlights the whole calendar month a day belongs to —
  // for a "which month?" picker (e.g. the Game Announcement Monthly period field).
  highlightMonth = false,
  // Opt-in: highlights a start/end date RANGE — the band from `rangeAnchor` to whichever
  // day is hovered (a live preview) or already selected as `value` (once both ends exist).
  // For a Custom start/end date pair: pass the OTHER field's current value as rangeAnchor,
  // so e.g. the End Date picker previews the growing range as the admin hovers past the
  // already-picked Start Date, and shows the full range once both are set. With no
  // rangeAnchor yet (the other date hasn't been picked), this picker just behaves like a
  // normal single-day picker — there's nothing to draw a range against yet.
  highlightRange = false,
  rangeAnchor = null,
  // Opt-in: blocks whole date RANGES (e.g. an existing announcement's [start, end]) rather
  // than a single min/max boundary — [{ startStr, endStr }, ...]. Rendered distinctly from a
  // plain out-of-range day (reddish tint + dot, not just greyed out) so "this date is already
  // taken by something else" reads differently from "this date isn't a valid choice at all".
  disabledRanges = [],
  // What a blocked day says on hover: the thing that holds it differs per screen (an announcement, a quiz).
  clashTitle = 'Already has an announcement',
}) => {
  const today = new Date().toISOString().slice(0, 10);
  const localPlaceholder = useLocalText(placeholder);

  const [anchorEl, setAnchorEl] = useState(null);
  const [viewYear,  setViewYear]  = useState(() => value ? parseInt(value.slice(0, 4)) : new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => value ? parseInt(value.slice(5, 7)) - 1 : new Date().getMonth());
  // 'day' | 'month' | 'year' — only ever leaves 'day' when enableYearNav is set.
  const [pickerView, setPickerView] = useState('day');
  const [yearPageStart, setYearPageStart] = useState(() => {
    const y = value ? parseInt(value.slice(0, 4)) : new Date().getFullYear();
    return y - (y % YEAR_GRID_SIZE);
  });

  const [hoveredDateStr, setHoveredDateStr] = useState(null);

  const open = Boolean(anchorEl);

  const highlightActive = highlightWeek || highlightMonth || highlightRange;

  const getBand = (dateStr) => {
    if (!dateStr) return null;
    if (highlightWeek) return getWeekRange(dateStr);
    if (highlightMonth) return getMonthRange(dateStr);
    if (highlightRange && rangeAnchor) {
      return rangeAnchor <= dateStr
        ? { startStr: rangeAnchor, endStr: dateStr }
        : { startStr: dateStr, endStr: rangeAnchor };
    }
    return null;
  };

  // With no value yet (e.g. End Date not picked yet) but an anchor already set (Start
  // Date), still mark the anchor day itself so it's visible as a reference point before
  // the admin hovers anything — otherwise this picker shows nothing highlighted at all
  // until the mouse moves, even though there's clearly already a start point to build from.
  const selectedBand = getBand(value)
    ?? (highlightRange && rangeAnchor ? { startStr: rangeAnchor, endStr: rangeAnchor } : null);
  const hoveredBand = getBand(hoveredDateStr);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    setPickerView('day');
    setHoveredDateStr(null);
    // With no value of its own yet, jump to the range anchor's month (e.g. Start Date's)
    // rather than today's — so the anchor's reference highlight is visible right away
    // instead of requiring a manual navigate-back.
    // With nothing to jump to it opens on today, unless today is outside the allowed range (min/max): then it
    // opens on the nearest allowed month, so the admin doesn't land on a month where every day is disabled.
    const nearestAllowed = (min && min > today) ? min : ((max && max < today) ? max : null);
    const jumpTo = value || (highlightRange && rangeAnchor) || nearestAllowed;
    if (jumpTo) {
      const y = parseInt(jumpTo.slice(0, 4));
      setViewYear(y);
      setViewMonth(parseInt(jumpTo.slice(5, 7)) - 1);
      setYearPageStart(y - (y % YEAR_GRID_SIZE));
    } else {
      const now = new Date();
      setViewYear(now.getFullYear());
      setViewMonth(now.getMonth());
      setYearPageStart(now.getFullYear() - (now.getFullYear() % YEAR_GRID_SIZE));
    }
  };

  const handleClose = () => { setAnchorEl(null); setHoveredDateStr(null); };

  const prevMonth = () => {
    if (pickerView === 'year') { setYearPageStart((s) => s - YEAR_GRID_SIZE); return; }
    if (pickerView === 'month') { setViewYear((y) => y - 1); return; }
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (pickerView === 'year') { setYearPageStart((s) => s + YEAR_GRID_SIZE); return; }
    if (pickerView === 'month') { setViewYear((y) => y + 1); return; }
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // Header label click cycles day -> month -> year, then back to day on selection.
  const toggleHeaderView = (target) => {
    if (!enableYearNav) return;
    setPickerView((cur) => (cur === target ? 'day' : target));
  };

  const handleSelectDay = (day) => {
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onChange({ target: { value: dateStr } });
    handleClose();
  };

  const handleClear = () => {
    onChange({ target: { value: '' } });
    handleClose();
  };

  const handleToday = () => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    setPickerView('day');
    onChange({ target: { value: today } });
    handleClose();
  };

  const firstDayOfWeek  = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth     = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

  const isOutOfRange = (dateStr) => (min && dateStr < min) || (max && dateStr > max);
  // Checked independently of isOutOfRange so the two can be styled differently — a range
  // clash still "wins" visually even if it also happens to sit outside min/max.
  const isClashDate = (dateStr) => disabledRanges.some((r) => inBand(r, dateStr));
  const isDisabled = (dateStr) => isOutOfRange(dateStr) || isClashDate(dateStr);
  // Is a red (booked) day visible in this month? Past days show greyed instead, so they don't count.
  const hasBookedDays = disabledRanges.length > 0
    && Array.from({ length: daysInMonth }, (_, i) => dateStrOf(viewYear, viewMonth, i + 1))
      .some((dateStr) => !isOutOfRange(dateStr) && isClashDate(dateStr));

  return (
    <>
      <Box sx={{ display: 'inline-flex', flexDirection: 'column', width: fullWidth ? '100%' : undefined }}>
        {/* ── Trigger field ─────────────────────────────────────────── */}
        <Box
          onClick={disabled ? undefined : handleOpen}
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            width: fullWidth ? '100%' : undefined,
            minWidth: fullWidth ? undefined : 148,
            height: 36,
            px: 1.5,
            gap: 0.875,
            border: `1.5px solid ${isError ? '#ef4444' : open ? '#0a326d' : value ? '#93c5fd' : '#dde6f0'}`,
            borderRadius: '10px',
            bgcolor: disabled ? '#f1f5f9' : (open || value ? '#eff6ff' : 'white'),
            cursor: disabled ? 'not-allowed' : 'pointer',
            userSelect: 'none',
            boxSizing: 'border-box',
            transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
            boxShadow: open ? '0 0 0 3px rgba(10,50,109,0.1)' : 'none',
            ...(!disabled && { '&:hover': { borderColor: isError ? '#f87171' : '#0a326d', bgcolor: '#eff6ff' } }),
          }}
        >
          <CalendarTodayOutlinedIcon
            sx={{ fontSize: 13, color: disabled ? '#cbd5e1' : (value || open ? '#0a326d' : '#94a3b8'), flexShrink: 0 }}
          />
          <Typography
            component="span"
            fontSize={12.5}
            fontWeight={value ? 600 : 400}
            sx={{ color: disabled ? '#94a3b8' : (value ? '#1e293b' : '#94a3b8'), whiteSpace: 'nowrap', flex: 1 }}
          >
            {fmtDisplay(value) || localPlaceholder}
          </Typography>
          {value && !disabled && (
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#0a326d', flexShrink: 0 }} />
          )}
        </Box>
        {helperText && (
          <Typography sx={{ fontSize: 11, mt: 0.5, px: 0.25, color: isError ? '#ef4444' : '#94a3b8' }}>
            <LocalText>{helperText}</LocalText>
          </Typography>
        )}
      </Box>

      {/* ── Calendar Popover ─────────────────────────────────────────── */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.75,
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid #e2e8f0',
            boxShadow: '0 24px 64px -12px rgba(10,50,109,0.28), 0 4px 16px rgba(0,0,0,0.06)',
            width: 296,
          },
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            background: '#0a326d',
            px: 2.5, py: 1.875,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <IconButton
            size="small"
            onClick={prevMonth}
            sx={{
              color: 'rgba(255,255,255,0.75)', width: 30, height: 30,
              '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.14)' },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              fontWeight={800}
              fontSize={15}
              onClick={() => toggleHeaderView('month')}
              sx={{
                color: 'white', lineHeight: 1.2, letterSpacing: '-0.2px',
                ...(enableYearNav && {
                  cursor: 'pointer', borderRadius: '6px', px: 0.75,
                  bgcolor: pickerView === 'month' ? 'rgba(255,255,255,0.18)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' },
                }),
              }}
            >
              <LocalText>{MONTHS[viewMonth]}</LocalText>
            </Typography>
            <Typography
              fontSize={11}
              fontWeight={600}
              onClick={() => toggleHeaderView('year')}
              sx={{
                color: 'rgba(255,255,255,0.6)', mt: 0.2,
                ...(enableYearNav && {
                  cursor: 'pointer', borderRadius: '6px', px: 0.75,
                  bgcolor: pickerView === 'year' ? 'rgba(255,255,255,0.18)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.14)', color: 'white' },
                }),
              }}
            >
              {pickerView === 'year' ? `${yearPageStart} – ${yearPageStart + YEAR_GRID_SIZE - 1}` : viewYear}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={nextMonth}
            sx={{
              color: 'rgba(255,255,255,0.75)', width: 30, height: 30,
              '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.14)' },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* ── Year grid (enableYearNav only) ── */}
        {pickerView === 'year' && (
          <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
            {Array.from({ length: YEAR_GRID_SIZE }, (_, i) => {
              const yr  = yearPageStart + i;
              const sel = yr === viewYear;
              return (
                <Box
                  key={yr}
                  onClick={() => { setViewYear(yr); setPickerView('month'); }}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    height: 42, borderRadius: '8px', cursor: 'pointer',
                    bgcolor: sel ? '#0a326d' : 'transparent',
                    border: '1.5px solid transparent',
                    transition: 'all 0.12s',
                    ...(!sel && { '&:hover': { bgcolor: '#dbeafe', borderColor: '#93c5fd' } }),
                  }}
                >
                  <Typography fontSize={13} fontWeight={sel ? 700 : 500} sx={{ color: sel ? 'white' : '#1e293b' }}>
                    {yr}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* ── Month grid (enableYearNav only) ── */}
        {pickerView === 'month' && (
          <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '4px' }}>
            {MONTHS.map((m, i) => {
              const sel = i === viewMonth;
              return (
                <Box
                  key={m}
                  onClick={() => { setViewMonth(i); setPickerView('day'); }}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    height: 42, borderRadius: '8px', cursor: 'pointer',
                    bgcolor: sel ? '#0a326d' : 'transparent',
                    border: '1.5px solid transparent',
                    transition: 'all 0.12s',
                    ...(!sel && { '&:hover': { bgcolor: '#dbeafe', borderColor: '#93c5fd' } }),
                  }}
                >
                  <Typography fontSize={12} fontWeight={sel ? 700 : 500} sx={{ color: sel ? 'white' : '#1e293b' }}>
                    <LocalText>{m.slice(0, 3)}</LocalText>
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* ── Day-of-week headers ── */}
        {pickerView === 'day' && (
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.25, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {DAY_LABELS.map((d, i) => (
            <Typography
              key={d}
              fontSize={9.5}
              fontWeight={700}
              sx={{
                textAlign: 'center',
                color: i === 0 || i === 6 ? '#94a3b8' : '#cbd5e1',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                pb: 0.625,
              }}
            >
              <LocalText>{d}</LocalText>
            </Typography>
          ))}
        </Box>
        )}

        {pickerView === 'day' && <Box sx={{ mx: 1.5, height: '1px', bgcolor: '#f1f5f9' }} />}

        {/* ── Days grid ── */}
        {pickerView === 'day' && (
        <Box
          sx={{ px: 1.5, py: 1.125, display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}
          onMouseLeave={() => highlightActive && setHoveredDateStr(null)}
        >
          {/* Leading grey days from previous month — given a real dateStr too (not just a
              decorative number) so the band renders correctly when the selected/hovered
              week, month, or range spans across the month boundary. Still not clickable —
              only this month's own days are selectable, unchanged from before. */}
          {(() => {
            const prevMonthIdx = viewMonth === 0 ? 11 : viewMonth - 1;
            const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
            return Array.from({ length: firstDayOfWeek }, (_, i) => {
              const day = daysInPrevMonth - firstDayOfWeek + 1 + i;
              const dateStr = dateStrOf(prevYear, prevMonthIdx, day);
              const col = i;
              const dis = isDisabled(dateStr);
              const inSelected = inBand(selectedBand, dateStr);
              const inHovered = !inSelected && inBand(hoveredBand, dateStr);
              return (
                <Box
                  key={`p${i}`}
                  onMouseEnter={() => highlightActive && !dis && setHoveredDateStr(dateStr)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', height: 34,
                    borderRadius: inSelected ? bandCellRadius(selectedBand, dateStr, col) : inHovered ? bandCellRadius(hoveredBand, dateStr, col) : 0,
                    bgcolor: inSelected ? (isClashDate(dateStr) ? OVERLAP_BG : '#0a326d') : inHovered ? 'rgba(59,130,246,0.10)' : 'transparent',
                  }}
                >
                  <Typography fontSize={12} sx={{ color: inSelected ? 'rgba(255,255,255,0.6)' : '#e2e8f0' }}>
                    {day}
                  </Typography>
                </Box>
              );
            });
          })()}

          {/* Current month days */}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day     = i + 1;
            const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isToday = dateStr === today;
            const outOfRange = isOutOfRange(dateStr);
            // min/max wins visually if a date somehow triggers both — "not a valid choice at
            // all" is the more fundamental reason, "already taken" is secondary in that case.
            const clash   = !outOfRange && isClashDate(dateStr);
            const dis     = outOfRange || clash;
            const col     = (firstDayOfWeek + i) % 7;
            const weekend = col === 0 || col === 6;
            // Includes the exact selected day itself — the whole band renders as one
            // uniform solid block, not a lighter tint around a single dark cell, per
            // direct feedback that it should look and feel like the actual selection,
            // not a preview of it.
            const inSelectedBand = inBand(selectedBand, dateStr);
            const sel = highlightActive ? inSelectedBand : dateStr === value;
            const inHoveredBand = !inSelectedBand && inBand(hoveredBand, dateStr);

            return (
              <Box
                key={day}
                title={clash ? clashTitle : undefined}
                onClick={() => !dis && handleSelectDay(day)}
                onMouseEnter={() => highlightActive && !dis && setHoveredDateStr(dateStr)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: 34, position: 'relative',
                  cursor: dis ? 'not-allowed' : 'pointer',
                  // A clash date is always its own standalone badge, never part of a merged
                  // select/hover band — give it the normal rounded-square radius regardless of
                  // highlightActive's band-merging logic (which would otherwise flatten it to a
                  // square, since it's neither the selected nor hovered cell).
                  borderRadius: clash && !sel
                    ? '8px'
                    : highlightActive
                      ? (sel ? bandCellRadius(selectedBand, dateStr, col) : inHoveredBand ? bandCellRadius(hoveredBand, dateStr, col) : 0)
                      : '8px',
                  bgcolor: sel ? (clash ? OVERLAP_BG : '#0a326d') : clash ? '#fef2f2' : inHoveredBand ? 'rgba(59,130,246,0.10)' : 'transparent',
                  border: clash && !sel ? '1.5px solid #fecaca' : isToday && !sel ? '1.5px solid #0a326d' : '1.5px solid transparent',
                  transition: 'all 0.12s',
                  ...(!dis && !sel && !highlightActive && {
                    '&:hover': { bgcolor: '#dbeafe', borderColor: '#93c5fd' },
                  }),
                }}
              >
                <Typography
                  fontSize={12.5}
                  fontWeight={sel || isToday ? 700 : weekend ? 500 : 400}
                  sx={{
                    color: sel       ? 'white'
                         : clash     ? '#fca5a5'
                         : outOfRange ? '#d1d5db'
                         : isToday   ? '#0a326d'
                         : weekend   ? '#94a3b8'
                         : '#1e293b',
                    textDecoration: clash && !sel ? 'line-through' : 'none',
                    lineHeight: 1,
                  }}
                >
                  {day}
                </Typography>
                {isToday && !sel && (
                  <Box
                    sx={{
                      position: 'absolute', bottom: 4, left: '50%',
                      transform: 'translateX(-50%)',
                      width: 3, height: 3, borderRadius: '50%', bgcolor: '#0a326d',
                    }}
                  />
                )}
              </Box>
            );
          })}

          {/* Trailing grey days to fill last row — same real-dateStr treatment as the
              leading cells above. */}
          {(() => {
            const total = firstDayOfWeek + daysInMonth;
            let remainder = total % 7 === 0 ? 0 : 7 - (total % 7);
            const nextMonthIdx = viewMonth === 11 ? 0 : viewMonth + 1;
            const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;

            // A Monday-Sunday week's closing Sunday sometimes falls just past this month's
            // last rendered row (e.g. a week starting in the month's last few days) — the
            // grid otherwise cuts it off entirely rather than just leaving it unhighlighted.
            // Extend by one more row so the full selected/hovered week is always visible,
            // not only its first 6 days. Scoped to highlightWeek: a month band never extends
            // past the month it belongs to, and a range preview can extend arbitrarily far
            // (like any date-range picker, only the portion within the visible month shows).
            if (highlightWeek) {
              const lastCoveredMs = msOf(dateStrOf(viewYear, viewMonth, daysInMonth)) + remainder * MS_PER_DAY;
              const bandEndStrs = [selectedBand?.endStr, hoveredBand?.endStr].filter(Boolean);
              const needsExtraRow = bandEndStrs.some((endStr) => {
                const endMs = msOf(endStr);
                return endMs > lastCoveredMs && endMs - lastCoveredMs <= 7 * MS_PER_DAY;
              });
              if (needsExtraRow) remainder += 7;
            }
            return Array.from({ length: remainder }, (_, i) => {
              const day = i + 1;
              const dateStr = dateStrOf(nextYear, nextMonthIdx, day);
              const col = (firstDayOfWeek + daysInMonth + i) % 7;
              const dis = isDisabled(dateStr);
              const inSelected = inBand(selectedBand, dateStr);
              const inHovered = !inSelected && inBand(hoveredBand, dateStr);
              return (
                <Box
                  key={`n${i}`}
                  onMouseEnter={() => highlightActive && !dis && setHoveredDateStr(dateStr)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', height: 34,
                    borderRadius: inSelected ? bandCellRadius(selectedBand, dateStr, col) : inHovered ? bandCellRadius(hoveredBand, dateStr, col) : 0,
                    bgcolor: inSelected ? (isClashDate(dateStr) ? OVERLAP_BG : '#0a326d') : inHovered ? 'rgba(59,130,246,0.10)' : 'transparent',
                  }}
                >
                  <Typography fontSize={12} sx={{ color: inSelected ? 'rgba(255,255,255,0.6)' : '#e2e8f0' }}>{day}</Typography>
                </Box>
              );
            });
          })()}
        </Box>
        )}

        {/* ── Legend: what the red days are, shown only while the month on screen has some ── */}
        {pickerView === 'day' && hasBookedDays && (
          <Box sx={{ px: 2, pb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: OVERLAP_BG }} />
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: OVERLAP_BG }}>
              <LocalText>Already booked</LocalText>
            </Typography>
          </Box>
        )}

        {/* ── Footer ── */}
        <Box
          sx={{
            px: 2, py: 1.25,
            borderTop: '1px solid #f1f5f9',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <Button
            size="small"
            onClick={handleClear}
            sx={{
              fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'none',
              px: 1.5, py: 0.5, borderRadius: '6px', minWidth: 0,
              '&:hover': { bgcolor: '#f1f5f9', color: '#475569' },
            }}
          >
            <LocalText>Clear</LocalText>
          </Button>
          <Button
            size="small"
            onClick={handleToday}
            sx={{
              fontSize: 11, fontWeight: 700, color: '#0a326d', textTransform: 'none',
              px: 1.5, py: 0.5, borderRadius: '6px', minWidth: 0,
              '&:hover': { bgcolor: '#eff6ff' },
            }}
          >
            <LocalText>Today</LocalText>
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default CustomDatePicker;
